/**
 * StealthColumn - Stealth's pane of the right column: the ladder (with the
 * alarm), the tracker, the director's Contacts list, the group approach, the
 * opposition, and what the party has learned. The tracker's blips and rings
 * are drawn BEHIND the whole column, in its gutter (TrackerGutter, 2026-10-21):
 * the column scrolls inside a frame that does not.
 *
 * @param {object} props
 * @param {object} props.stealth - hooks/useShellStealth's `stealth`
 * @param {object} [props.fight] - `{ sides, onStart }`, for the alarm card
 * @param {object} [props.approach] - hooks/useShellApproach; absent, no approach panel is drawn
 * @param {object} [props.contacts] - hooks/useStealthContacts' `contacts`: the director's list
 * @param {(id: string) => void} [props.onReveal] - the GM's reveal, for the list's Revealed
 */
import { useState } from 'react';
import common from '../../styles/common.module.css';
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';
import ContactsList from './ContactsList';
import LadderPanel from './LadderPanel';
import OppositionPanel from './OppositionPanel';
import ApproachPanel from './ApproachPanel';
import TrackerPanel, { TrackerGutter } from './TrackerPanel';

/** The director's page, with a Save: Ctrl+Enter, the button, or leaving the field (Enter is a new line). */
function LearnedField({ stored, onSave }) {
  const [draft, setDraft] = useState(stored);
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setDraft(stored);
  }
  const pending = draft !== stored;
  const save = () => pending && onSave(draft);
  return (
    <>
      <textarea
        className={story.textarea}
        aria-label="What the party has learned"
        data-testid="learned-input"
        placeholder="Six guards, lasguns. One vox-caster by the gate."
        value={draft}
        maxLength={2000}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && (e.ctrlKey || e.metaKey) && save()}
        onBlur={save}
      />
      <div className={story.tools}>
        <button type="button" className={common.btnSmall} data-testid="learned-save" disabled={!pending} onClick={save}>
          Save
        </button>
        {pending && (
          <span className={story.empty} data-testid="learned-unsaved" aria-live="polite">
            Not saved yet
          </span>
        )}
      </div>
    </>
  );
}

/** What the party has learned: the director's page, read by everyone. */
function Learned({ stealth }) {
  const stored = stealth.state.learned;
  return (
    <section className={story.section} aria-label="What you have learned" data-testid="learned-panel">
      <h3 className={story.caption}>What you have learned</h3>
      {stealth.canDirect ? (
        <LearnedField stored={stored} onSave={stealth.setLearned} />
      ) : (
        <p className={stored ? story.body : story.empty} data-testid="learned-text">
          {stored || 'Nothing yet.'}
        </p>
      )}
    </section>
  );
}

export default function StealthColumn({ stealth, fight, approach, contacts, onReveal }) {
  return (
    <div className={styles.trackerFrame}>
      <TrackerGutter contacts={stealth.state.contacts} />
      <div className={story.column} data-testid="stealth-column">
        <LadderPanel stealth={stealth} fight={fight} />
        <TrackerPanel contacts={stealth.state.contacts} />
        {stealth.canDirect && <ContactsList contacts={contacts} onReveal={onReveal} />}
        {approach && <ApproachPanel approach={approach} someoneCanLook={!!stealth.sharpest} />}
        <OppositionPanel stealth={stealth} />
        <Learned stealth={stealth} />
      </div>
    </div>
  );
}
